import { Link, useLocation } from 'react-router-dom'
import {
  LayoutGrid,
  Users,
  Scale,
  Briefcase,
  FileText,
  FileSignature,
  Mail,
  Calendar,
  CheckSquare,
  Clock,
  Receipt,
  Coins,
  Wallet,
  BookOpen,
  Calculator,
  Boxes,
  NotebookText,
  Scale as ScaleIcon,
  ListTree,
  Sigma,
  Hourglass,
  Percent,
  HandCoins,
  PiggyBank,
  Sparkles,
  ScanText,
  Settings as SettingsIcon,
  Sun,
  Moon,
  type LucideIcon,
} from 'lucide-react'
import { useTheme } from '../theme/ThemeContext'
import { useAuth } from '../contexts/AuthContext'
import { useIssuersState } from '../components/accounting/common'
import { issuerHasBooks, issuerHasPgc } from './../lib/ctb'

type Accent =
  | 'toga'
  | 'cyan'
  | 'purple'
  | 'pink'
  | 'orange'
  | 'rose'
  | 'emerald'
  | 'indigo'
  | 'amber'
  | 'sky'
  | 'blue'
  | 'fuchsia'

interface NavItemDef {
  to: string
  label: string
  icon: LucideIcon
  accent: Accent
  /** Cuelga visualmente del anterior: más pequeño y sangrado (no es un nivel plegable). */
  child?: boolean
  /** Cuándo se marca como activo; por defecto, la ruta exacta o una subruta. */
  active?: (pathname: string) => boolean
}

interface NavGroup {
  label: string
  items: NavItemDef[]
}

/**
 * El menú. Las pantallas de gastos, inversiones y borradores 303/130 solo existen para ADMIN y
 * ABOGADO cuando algún emisor lleva libros del autónomo (`booksAvailable`): si no, no se ofrecen.
 */
function buildGroups(booksAvailable: boolean, pgcAvailable: boolean): NavGroup[] {
  return [
    {
      label: 'PRINCIPAL',
      items: [
        { to: '/dashboard', label: 'Dashboard', icon: LayoutGrid, accent: 'toga' },
        { to: '/inbox', label: 'Buzón', icon: Mail, accent: 'sky' },
      ],
    },
    {
      label: 'GESTIÓN',
      items: [
        { to: '/clients', label: 'Clientes', icon: Users, accent: 'purple' },
        { to: '/cases', label: 'Expedientes', icon: Scale, accent: 'rose' },
        { to: '/documents', label: 'Documentos', icon: FileText, accent: 'blue' },
        { to: '/procuradores', label: 'Procuradores', icon: Briefcase, accent: 'cyan' },
      ],
    },
    {
      label: 'AGENDA',
      items: [
        { to: '/agenda', label: 'Calendario', icon: Calendar, accent: 'emerald' },
        { to: '/tasks', label: 'Tareas', icon: CheckSquare, accent: 'amber' },
        { to: '/deadlines', label: 'Plazos', icon: Clock, accent: 'orange' },
      ],
    },
    {
      label: 'FACTURACIÓN',
      items: [
        { to: '/quotes', label: 'Presupuestos', icon: FileSignature, accent: 'orange' },
        {
          to: '/billing',
          label: 'Facturas emitidas',
          icon: Receipt,
          accent: 'pink',
          // «Pendientes de facturar» cuelga de aquí pero tiene su propia entrada.
          active: (p) =>
            (p === '/billing' || p.startsWith('/billing/')) && !p.startsWith('/billing/pending'),
        },
        {
          to: '/billing/pending',
          label: 'Pendientes de facturar',
          icon: Hourglass,
          accent: 'pink',
          child: true,
        },
        ...(booksAvailable
          ? [
              {
                to: '/accounting/expenses',
                label: 'Gastos',
                icon: Wallet,
                accent: 'amber' as const,
              },
            ]
          : []),
        { to: '/rates', label: 'Tarifas', icon: Coins, accent: 'indigo' },
      ],
    },
    {
      label: 'CONTABILIDAD',
      items: [
        ...(pgcAvailable
          ? [
              {
                to: '/ctb/journal',
                label: 'Diario',
                icon: NotebookText,
                accent: 'indigo' as const,
              },
              {
                to: '/ctb/ledger',
                label: 'Libro mayor',
                icon: ScaleIcon,
                accent: 'indigo' as const,
                child: true,
              },
              {
                to: '/ctb/trial-balance',
                label: 'Sumas y saldos',
                icon: Sigma,
                accent: 'sky' as const,
                child: true,
              },
              {
                to: '/ctb/accounts',
                label: 'Plan de cuentas',
                icon: ListTree,
                accent: 'sky' as const,
                child: true,
              },
            ]
          : []),
        { to: '/accounting/ledger', label: 'Facturas expedidas', icon: BookOpen, accent: 'indigo' },
        {
          to: '/accounting/vat',
          label: 'IVA por trimestre',
          icon: Percent,
          accent: 'sky',
          child: true,
        },
        {
          to: '/accounting/receivables',
          label: 'Cobros pendientes',
          icon: HandCoins,
          accent: 'emerald',
          child: true,
        },
        {
          to: '/accounting/funds',
          label: 'Provisiones y suplidos',
          icon: PiggyBank,
          accent: 'amber',
          child: true,
        },
        ...(booksAvailable
          ? [
              {
                to: '/accounting/investments',
                label: 'Bienes de inversión',
                icon: Boxes,
                accent: 'amber' as const,
              },
              {
                to: '/accounting/taxes',
                label: 'Resultado e impuestos',
                icon: Calculator,
                accent: 'fuchsia' as const,
              },
            ]
          : []),
      ],
    },
    {
      label: 'INTELIGENCIA',
      items: [
        { to: '/ai', label: 'Asistente IA', icon: Sparkles, accent: 'fuchsia' },
        { to: '/ocr', label: 'OCR', icon: ScanText, accent: 'cyan' },
      ],
    },
  ]
}

interface AccentDef {
  iconDark: string
  iconLight: string
  bgDark: string
  bgLight: string
  textDark: string
  textLight: string
  bar: string
  glow: string
}

const accentMap: Record<Accent, AccentDef> = {
  toga: {
    iconDark: 'text-toga-300',
    iconLight: 'text-toga-700',
    bgDark: 'bg-toga-500/15',
    bgLight: 'bg-toga-500/10',
    textDark: 'text-toga-200',
    textLight: 'text-toga-800',
    bar: 'from-toga-300 to-toga-500',
    glow: 'rgba(191,43,70,0.55)',
  },
  cyan: {
    iconDark: 'text-cyan-300',
    iconLight: 'text-cyan-600',
    bgDark: 'bg-cyan-500/15',
    bgLight: 'bg-cyan-500/10',
    textDark: 'text-cyan-200',
    textLight: 'text-cyan-700',
    bar: 'from-cyan-300 to-cyan-500',
    glow: 'rgba(34,211,238,0.55)',
  },
  purple: {
    iconDark: 'text-purple-300',
    iconLight: 'text-purple-600',
    bgDark: 'bg-purple-500/15',
    bgLight: 'bg-purple-500/10',
    textDark: 'text-purple-200',
    textLight: 'text-purple-700',
    bar: 'from-purple-300 to-purple-500',
    glow: 'rgba(168,85,247,0.55)',
  },
  pink: {
    iconDark: 'text-pink-300',
    iconLight: 'text-pink-600',
    bgDark: 'bg-pink-500/15',
    bgLight: 'bg-pink-500/10',
    textDark: 'text-pink-200',
    textLight: 'text-pink-700',
    bar: 'from-pink-300 to-pink-500',
    glow: 'rgba(244,114,182,0.55)',
  },
  orange: {
    iconDark: 'text-orange-300',
    iconLight: 'text-orange-600',
    bgDark: 'bg-orange-500/15',
    bgLight: 'bg-orange-500/10',
    textDark: 'text-orange-200',
    textLight: 'text-orange-700',
    bar: 'from-orange-300 to-orange-500',
    glow: 'rgba(251,146,60,0.55)',
  },
  rose: {
    iconDark: 'text-rose-300',
    iconLight: 'text-rose-600',
    bgDark: 'bg-rose-500/15',
    bgLight: 'bg-rose-500/10',
    textDark: 'text-rose-200',
    textLight: 'text-rose-700',
    bar: 'from-rose-300 to-rose-500',
    glow: 'rgba(244,63,94,0.55)',
  },
  emerald: {
    iconDark: 'text-emerald-300',
    iconLight: 'text-emerald-600',
    bgDark: 'bg-emerald-500/15',
    bgLight: 'bg-emerald-500/10',
    textDark: 'text-emerald-200',
    textLight: 'text-emerald-700',
    bar: 'from-emerald-300 to-emerald-500',
    glow: 'rgba(52,211,153,0.55)',
  },
  indigo: {
    iconDark: 'text-indigo-300',
    iconLight: 'text-indigo-600',
    bgDark: 'bg-indigo-500/15',
    bgLight: 'bg-indigo-500/10',
    textDark: 'text-indigo-200',
    textLight: 'text-indigo-700',
    bar: 'from-indigo-300 to-indigo-500',
    glow: 'rgba(129,140,248,0.55)',
  },
  amber: {
    iconDark: 'text-amber-300',
    iconLight: 'text-amber-600',
    bgDark: 'bg-amber-500/15',
    bgLight: 'bg-amber-500/10',
    textDark: 'text-amber-200',
    textLight: 'text-amber-700',
    bar: 'from-amber-300 to-amber-500',
    glow: 'rgba(251,191,36,0.55)',
  },
  sky: {
    iconDark: 'text-sky-300',
    iconLight: 'text-sky-600',
    bgDark: 'bg-sky-500/15',
    bgLight: 'bg-sky-500/10',
    textDark: 'text-sky-200',
    textLight: 'text-sky-700',
    bar: 'from-sky-300 to-sky-500',
    glow: 'rgba(56,189,248,0.55)',
  },
  blue: {
    iconDark: 'text-blue-300',
    iconLight: 'text-blue-600',
    bgDark: 'bg-blue-500/15',
    bgLight: 'bg-blue-500/10',
    textDark: 'text-blue-200',
    textLight: 'text-blue-700',
    bar: 'from-blue-300 to-blue-500',
    glow: 'rgba(96,165,250,0.55)',
  },
  fuchsia: {
    iconDark: 'text-fuchsia-300',
    iconLight: 'text-fuchsia-600',
    bgDark: 'bg-fuchsia-500/15',
    bgLight: 'bg-fuchsia-500/10',
    textDark: 'text-fuchsia-200',
    textLight: 'text-fuchsia-700',
    bar: 'from-fuchsia-300 to-fuchsia-500',
    glow: 'rgba(232,121,249,0.55)',
  },
}

interface NavItemProps extends NavItemDef {
  isActive: boolean
  isLight: boolean
}

function NavItem({ to, label, icon: Icon, accent, isActive, isLight, child }: NavItemProps) {
  const a = accentMap[accent]
  const activeBg = isLight ? `${a.bgLight} ${a.textLight}` : `${a.bgDark} ${a.textDark}`
  const idleColor = isLight
    ? 'text-slate-600 hover:text-slate-900 hover:bg-slate-200/60'
    : 'text-slate-300 hover:text-white hover:bg-slate-800/50'
  const iconColor = isActive
    ? isLight
      ? a.iconLight
      : a.iconDark
    : isLight
      ? 'text-slate-500 group-hover:text-slate-700'
      : 'text-slate-400 group-hover:text-slate-200'

  return (
    <Link
      to={to}
      className={[
        'group relative flex items-center gap-3 pr-3 rounded-lg font-medium transition-all',
        child ? 'pl-8 py-1.5 text-[13px]' : 'pl-4 py-2 text-sm',
        isActive ? activeBg : idleColor,
      ].join(' ')}
      style={isActive ? { boxShadow: `0 0 22px ${a.glow}` } : undefined}
    >
      {isActive && (
        <span
          className={`absolute left-0 top-1.5 bottom-1.5 w-1 rounded-r bg-gradient-to-b ${a.bar} shadow-[0_0_10px_rgba(255,255,255,0.4)]`}
        />
      )}
      <Icon size={child ? 15 : 18} className={iconColor} />
      <span>{label}</span>
    </Link>
  )
}

export default function Sidebar() {
  const { theme, toggle } = useTheme()
  const isLight = theme === 'light'
  const { pathname } = useLocation()
  const isSettings = pathname.startsWith('/settings')
  const auth = useAuth()
  const { issuers } = useIssuersState()
  const canSeeBooks =
    auth.status === 'authenticated' && (auth.user.role === 'ADMIN' || auth.user.role === 'ABOGADO')
  const booksAvailable = canSeeBooks && issuers.some(issuerHasBooks)
  const pgcAvailable = canSeeBooks && issuers.some(issuerHasPgc)
  const groups = buildGroups(booksAvailable, pgcAvailable)
  const isItemActive = (it: NavItemDef) =>
    it.active ? it.active(pathname) : pathname === it.to || pathname.startsWith(it.to + '/')

  return (
    <aside
      className="relative z-10 w-64 shrink-0 h-screen sticky top-0 flex flex-col backdrop-blur-md border-r"
      style={{ background: 'var(--bg-sidebar)', borderColor: 'var(--border)' }}
    >
      {/* Marca */}
      <div className="px-5 pt-6 pb-5">
        <Link to="/dashboard" className="flex flex-col group">
          <span
            className={[
              'text-3xl font-serif font-bold tracking-tight leading-none',
              isLight
                ? 'bg-clip-text text-transparent bg-gradient-to-r from-toga-700 via-toga-600 to-toga-800'
                : 'bg-clip-text text-transparent bg-gradient-to-r from-toga-200 via-toga-300 to-toga-500 drop-shadow-[0_0_10px_rgba(213,72,94,0.25)]',
            ].join(' ')}
          >
            Toga
          </span>
          <span
            className={[
              'mt-1 text-[12px] font-medium tracking-wide',
              isLight ? 'text-toga-700/80' : 'text-toga-300/85',
            ].join(' ')}
          >
            CRM para bufetes
          </span>
        </Link>
      </div>

      {/* Grupos de navegación */}
      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        {groups.map((g) => (
          <div key={g.label} className="mb-5">
            <div
              className={[
                'px-3 mb-2 text-[11px] font-semibold tracking-[0.18em]',
                isLight ? 'text-slate-400' : 'text-slate-500',
              ].join(' ')}
            >
              {g.label}
            </div>
            <div className="flex flex-col gap-1">
              {g.items.map((it) => (
                <NavItem key={it.to} {...it} isActive={isItemActive(it)} isLight={isLight} />
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Pie */}
      <div
        className="px-3 pb-5 pt-3 border-t flex flex-col gap-1"
        style={{ borderColor: 'var(--border)' }}
      >
        <Link
          to="/settings"
          className={[
            'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors',
            isSettings
              ? isLight
                ? 'bg-slate-200/70 text-slate-900'
                : 'bg-slate-800/70 text-white shadow-[inset_0_0_0_1px_rgba(148,163,184,0.2)]'
              : isLight
                ? 'text-slate-600 hover:bg-slate-200/50'
                : 'text-slate-300 hover:bg-slate-800/50',
          ].join(' ')}
        >
          <SettingsIcon size={18} className={isLight ? 'text-slate-500' : 'text-slate-400'} />
          <span className="font-medium">Ajustes</span>
        </Link>
        <button
          type="button"
          onClick={toggle}
          className={[
            'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-colors text-left',
            isLight
              ? 'text-slate-600 hover:bg-slate-200/50'
              : 'text-slate-300 hover:bg-slate-800/50',
          ].join(' ')}
        >
          {isLight ? (
            <Moon size={18} className="text-indigo-500" />
          ) : (
            <Sun size={18} className="text-amber-300" />
          )}
          <span
            className={['font-medium', isLight ? 'text-indigo-600' : 'text-amber-300'].join(' ')}
          >
            {isLight ? 'Modo oscuro' : 'Modo claro'}
          </span>
        </button>
      </div>
    </aside>
  )
}
